import { useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { loadRuntimeConfig, type Experience, type RuntimeConfig } from "./platform/runtime";
import { createPlatformClient, type PlatformClient } from "./platform/supabase";
import { CenterLearnersPage } from "./features/center/CenterLearnersPage";
import { CenterProgramsPage } from "./features/center/CenterProgramsPage";
import { CenterReportsPage } from "./features/center/CenterReportsPage";
import { CenterBadgeERPPage } from "./features/center/CenterBadgeERPPage";
import { CenterEvidenceWorkbench } from "./features/center/CenterEvidenceWorkbench";
import { CenterEvaluationAchievementWorkbench } from "./features/center/CenterEvaluationAchievementWorkbench";
import { CenterAssurancePage } from "./features/center/CenterAssurancePage";
import { CenterTodayPage } from "./features/center/CenterTodayPage";
import { CenterOperationsPage } from "./features/center/CenterOperationsPage";
import { CenterCaseloadPage } from "./features/center/CenterCaseloadPage";
import { CenterPrivacyPage } from "./features/center/CenterPrivacyPage";
import { CenterPartnersPage } from "./features/center/CenterPartnersPage";
import { CenterNotificationBell } from "./features/center/CenterNotificationBell";
import { CenterAssessmentsPage } from "./features/center/CenterAssessmentsPage";
import { CenterStudentLifecyclePage } from "./features/center/CenterStudentLifecyclePage";
import { CenterRosterPlanningPage } from "./features/center/CenterRosterPlanningPage";
import { CenterClassCounselingPage } from "./features/center/CenterClassCounselingPage";
import { CenterJourneysPage } from "./features/center/CenterJourneysPage";
import { CenterHelpPage } from "./features/center/CenterHelpPage";
import { CenterTooltipLayer, centerFriendlyNavCopy } from "./features/center/CenterFriendlyUX";
import {
  PublicWalletSharePage,
  WalletClaimPage,
  WalletBadgeEmailClaimPage,
  WalletCredentialsPage
} from "./features/wallet/WalletProduct";
import { WalletSharePrivacyStudio } from "./features/wallet/WalletSharePrivacyStudio";
import { WalletInboxActionCenter } from "./features/wallet/WalletInboxActionCenter";
import { WalletActivityBadgesPage } from "./features/wallet/WalletActivityBadgesPage";
import { WalletHelpCenter } from "./features/wallet/WalletHelpCenter";
import { WalletGrowthMapPage } from "./features/wallet/WalletGrowthMapPage";
import { WalletGoalPlannerPage } from "./features/wallet/WalletGoalPlannerPage";
import { AdminCredentialQueue } from "./features/admin/AdminCredentialQueue";
import { AdminActivityBadgeCertification } from "./features/admin/AdminActivityBadgeCertification";
import { AdminCompetencyStudio } from "./features/admin/AdminCompetencyStudio";
import { AdminAuditTrace } from "./features/admin/AdminAuditTrace";
import { AdminOrganizationsPage } from "./features/admin/AdminOrganizationsPage";
import { AdminAccessPage } from "./features/admin/AdminAccessPage";
import { AdminAssessmentStudio } from "./features/admin/AdminAssessmentStudio";
import { AdminOverviewPage } from "./features/admin/AdminOverviewPage";
import { AdminControlTowerPage } from "./features/admin/AdminControlTowerPage";
import { AdminAiGuide } from "./features/admin/AdminAiGuide";
import { AdminAiCostPage } from "./features/admin/AdminAiCostPage";
import { ExperienceAiGuide } from "./features/shared/ExperienceAiGuide";
import { AdminAssuranceGovernance } from "./features/admin/AdminAssuranceGovernance";
import { AdminBillingCenter } from "./features/admin/AdminBillingCenter";
import { AdminCommunicationsCenter } from "./features/admin/AdminCommunicationsCenter";
import { AdminDataMigrationCenter } from "./features/admin/AdminDataMigrationCenter";
import { AdminExecutiveAnalytics } from "./features/admin/AdminExecutiveAnalytics";
import { AdminKnowledgeBasePage } from "./features/admin/AdminKnowledgeBasePage";
import { AdminPlatformConfiguration } from "./features/admin/AdminPlatformConfiguration";
import { AdminPrivacyGovernance } from "./features/admin/AdminPrivacyGovernance";
import { AdminSchoolRollout } from "./features/admin/AdminSchoolRollout";
import { AdminSecurityControlCenter } from "./features/admin/AdminSecurityControlCenter";
import { AdminServiceDeskPage } from "./features/admin/AdminServiceDeskPage";
import { AdminSystemOperations } from "./features/admin/AdminSystemOperations";
import { AdminTenantContractCenter } from "./features/admin/AdminTenantContractCenter";
import { WalletAssessmentsPage } from "./features/wallet/WalletAssessmentsPage";
import { WalletJourneyTimelinePage } from "./features/wallet/WalletJourneyTimelinePage";
import { WalletExperienceStudioPage } from "./features/wallet/WalletExperienceStudio";
import { PortfolioStudioPage } from "./features/wallet/PortfolioStudio";
import "./features/wallet/WalletFutureVisualSystem.css";
import { StaffAccessClaimPage } from "./features/access/StaffAccessClaimPage";
import { GuardianConsentPublicPage, StudentDataSharePublicPage } from "./features/public/PrivacyPublicPages";
import { ProviderSessionPublicPage } from "./features/public/ProviderSessionPublicPage";
import { accessReasonText, appSubtitle, appTitle, credentialStatusLabel, environmentLabel, experienceLabel, roleLabel } from "./platform/locale";

import { CenterCalmHome } from "./features/center/calm/CenterCalmHome";
import { contextMatches, isCalmEntry } from "./features/center/calm/calm";
const CENTER_CALM_HOME_ENABLED = import.meta.env.VITE_CENTER_CALM_HOME === "true";

const AUTH_RETURN_KEY = "rs-career-erp-auth-return";
const WALLET_CLAIM_KEY = "rs-career-wallet-claim-token";
const BADGE_EMAIL_CLAIM_KEY = "rs-career-badge-email-claim-token";
const STAFF_CLAIM_KEY = "rs-career-staff-claim-token";
const PORTFOLIO_PREFILL_KEY = "rs-life-passport-portfolio-prefill";
const RS_ADMIN_ENTRY_KEY = "rs-career-erp-rs-admin-entry";
const RS_MASTER_ADMIN_EMAIL = "rs-master-admin@daily-coach-ing.com";

type Role = {
  roleKey: string;
  scopeType: string;
  scopeRef?: string | null;
};

type Workspace = {
  membershipId: string;
  membershipType: string;
  organizationId: string;
  organizationName: string;
  organizationType: string;
  roles: Role[];
};

type Me = {
  authUserId: string;
  personId: string;
  displayName: string;
  email?: string | null;
  accountStatus: string;
  memberships: Workspace[];
};

type NavigationItem = {
  routeKey: string;
  path: string;
  label: string;
  description?: string | null;
};

type ExperienceContext = {
  personId: string;
  experience: Experience;
  allowed: boolean;
  reason: string;
  workspace?: {
    organizationId: string;
    organizationName: string;
    organizationType: string;
  } | null;
  navigation: NavigationItem[];
  defaultPath: string;
};

type AvailableExperience = {
  experience: "ADMIN" | "CENTER" | "WALLET";
  allowed: boolean;
  domainKey: "admin" | "center" | "wallet";
};

type VerifyResult = {
  external_credential_id: string;
  status: string;
  is_valid: boolean;
  issued_at: string;
  expires_at?: string | null;
  credential_name: string;
  achievement_name: string;
  issuer_name: string;
  verification_uri?: string | null;
};

function normalizePath(pathname: string): string {
  if (!pathname || pathname === "/auth/callback") return "/";
  const value = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return value.length > 1 && value.endsWith("/") ? value.slice(0, -1) : value;
}

export function App() {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [client, setClient] = useState<PlatformClient | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [availableExperiences, setAvailableExperiences] = useState<AvailableExperience[]>([]);
  const [experienceContext, setExperienceContext] = useState<ExperienceContext | null>(null);
  const [workspaceId, setWorkspaceId] = useState("");
  const [currentPath, setCurrentPath] = useState(() => normalizePath(window.location.pathname));
  const [message, setMessage] = useState("");
  const [walletHelpOpen, setWalletHelpOpen] = useState(false);
  const [adminAiOpen, setAdminAiOpen] = useState(false);
  const [experienceAiOpen, setExperienceAiOpen] = useState(false);
  const [centerLegacyHome, setCenterLegacyHome] = useState(false);
  useEffect(() => { setCenterLegacyHome(false); }, [workspaceId, session?.user.id]);
  const [pendingStaffClaim] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token") || "";

    if (window.location.pathname === "/access/claim" && token) {
      window.sessionStorage.setItem(STAFF_CLAIM_KEY, token);
    }

    if (window.location.pathname === "/claim" && token) {
      window.sessionStorage.setItem(WALLET_CLAIM_KEY, token);
    }

    return window.sessionStorage.getItem(STAFF_CLAIM_KEY) || "";
  });

  const [pendingBadgeEmailClaim] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token") || "";

    if (window.location.pathname === "/email-claim" && token) {
      window.sessionStorage.setItem(BADGE_EMAIL_CLAIM_KEY, token);
    }

    return window.sessionStorage.getItem(BADGE_EMAIL_CLAIM_KEY) || "";
  });

  useEffect(() => {
    const onPopState = () => setCurrentPath(normalizePath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    let active = true;
    let subscription: { unsubscribe(): void } | undefined;

    (async () => {
      try {
        const runtime = await loadRuntimeConfig();
        if (!active) return;

        setConfig(runtime);
        document.documentElement.dataset.experience = runtime.experience.toLowerCase();

        const supabase = createPlatformClient(runtime);
        setClient(supabase);

        if (runtime.experience === "VERIFY") return;

        const { data } = await supabase.auth.getSession();
        if (!active) return;
        setSession(data.session);

        const authSubscription = supabase.auth.onAuthStateChange((_event, nextSession) => {
          setSession(nextSession);
        });
        subscription = authSubscription.data.subscription;
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "애플리케이션 설정을 불러오지 못했습니다.");
      }
    })();

    return () => {
      active = false;
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!client || !session || !config || config.experience === "VERIFY") {
      setMe(null);
      setAvailableExperiences([]);
      return;
    }

    (async () => {
      const rsAdminEntryRequested = window.sessionStorage.getItem(RS_ADMIN_ENTRY_KEY)==="1";
      if (rsAdminEntryRequested && session.user.app_metadata?.rs_master_admin !== true) {
        window.sessionStorage.removeItem(RS_ADMIN_ENTRY_KEY);
        setMessage("RS 관리자 계정이 아닙니다. RS 관리자 Google 계정으로 다시 로그인해 주세요.");
        await client.auth.signOut();
        return;
      }

      const identityResult = await client.rpc("ensure_my_identity");

      if (identityResult.error) {
        setMessage(identityResult.error.message);
        return;
      }

      const [meResult, experienceResult] = await Promise.all([
        client.rpc("me"),
        client.rpc("available_experiences")
      ]);

      if (meResult.error) {
        setMessage(meResult.error.message);
        return;
      }

      if (experienceResult.error) {
        setMessage(experienceResult.error.message);
        return;
      }

      const profile = meResult.data as Me;
      setMe(profile);
      setAvailableExperiences((experienceResult.data || []) as AvailableExperience[]);

      if (config?.experience === "CENTER") {
        const eligible = profile.memberships.filter((workspace) =>
          workspace.roles.some((role) =>
            role.roleKey === "CAREER_TEACHER" || role.roleKey === "SCHOOL_ADMIN"
          )
        );

        if (eligible.length === 1 || (rsAdminEntryRequested && eligible.length > 0)) {
          const preferred = eligible.find((workspace)=>!workspace.organizationName.startsWith("[QA]")) || eligible[0];
          setWorkspaceId(preferred.organizationId);
        }
      } else if (config.experience === "ADMIN") {
        const adminWorkspace = profile.memberships.find((workspace) =>
          workspace.roles.some((role) => role.roleKey === "PLATFORM_ADMIN" || role.roleKey === "REGIONAL_ADMIN")
        );
        if (adminWorkspace) setWorkspaceId(adminWorkspace.organizationId);
      }

      if (rsAdminEntryRequested) {
        window.sessionStorage.removeItem(RS_ADMIN_ENTRY_KEY);
      }
    })();
  }, [client, session, config]);

  useEffect(() => {
    if (!client || !session || !config || !me || config.experience === "VERIFY") {
      setExperienceContext(null);
      return;
    }

    let active = true;
    setExperienceContext(null);
    (async () => {
      const workspaceParam =
        config.experience === "CENTER" && workspaceId
          ? workspaceId
          : config.experience === "ADMIN" && workspaceId
            ? workspaceId
            : null;

      const { data, error } = await client.rpc("experience_context", {
        p_experience: config.experience,
        p_workspace_id: workspaceParam
      });

      if (!active) return;
      if (error) {
        setMessage(error.message);
        setExperienceContext(null);
        return;
      }

      setExperienceContext(data as ExperienceContext);
    })();
    return () => { active = false; };
  }, [client, session, config, me, workspaceId]);

  const workspaceOptions = useMemo(() => {
    if (!me || !config) return [];

    if (config.experience === "CENTER") {
      return me.memberships.filter((workspace) =>
        workspace.roles.some((role) =>
          role.roleKey === "CAREER_TEACHER" || role.roleKey === "SCHOOL_ADMIN"
        )
      );
    }

    if (config.experience === "ADMIN") {
      return me.memberships.filter((workspace) =>
        workspace.roles.some((role) => role.roleKey === "PLATFORM_ADMIN" || role.roleKey === "REGIONAL_ADMIN")
      );
    }

    return [];
  }, [me, config]);

  const currentWorkspace = useMemo(
    () => workspaceOptions.find((workspace) => workspace.organizationId === workspaceId) ?? null,
    [workspaceOptions, workspaceId]
  );
  const navigationItems = experienceContext?.navigation || [];
  const walletNavigationItems = navigationItems;

  useEffect(() => {
    if (!experienceContext?.allowed || experienceContext.navigation.length === 0) return;

    const allowedPaths = new Set(experienceContext.navigation.map((item) => item.path));
    if (config?.experience === "CENTER") {
      ["/evaluation","/assurance","/journeys","/help"].forEach((path)=>allowedPaths.add(path));
    }
    const storedReturn = window.sessionStorage.getItem(AUTH_RETURN_KEY);
    const candidate = storedReturn ? normalizePath(storedReturn) : currentPath;
    const isExperienceDetail = config?.experience === "WALLET"
      && /^\/experiences\/(school|personal)\/[0-9a-f-]{36}$/i.test(candidate);

    if (storedReturn) window.sessionStorage.removeItem(AUTH_RETURN_KEY);

    const nextPath = (allowedPaths.has(candidate) || isExperienceDetail)
      ? candidate
      : experienceContext.defaultPath || experienceContext.navigation[0].path;

    if (nextPath !== currentPath || window.location.pathname === "/auth/callback") {
      window.history.replaceState({}, "", nextPath);
      setCurrentPath(nextPath);
    }
  }, [experienceContext, currentPath, config]);

  function navigate(path: string) {
    const normalized = normalizePath(path);
    if (normalized === currentPath) return;
    window.history.pushState({}, "", normalized);
    setCurrentPath(normalized);
  }

  function useWalletRecordInPortfolio(
    sourceType: "LIFE_STAMP" | "EXPERIENCE" | "COMPETENCY" | "ACHIEVEMENT" | "GOAL" | "ASSESSMENT_RESULT",
    sourceId: string
  ) {
    window.sessionStorage.setItem(PORTFOLIO_PREFILL_KEY, JSON.stringify({
      sourceType,
      sourceId,
      documentType: sourceType === "EXPERIENCE" ? "ACTIVITY_RECORD" : "SELF_INTRODUCTION"
    }));
    navigate("/portfolio");
  }

  if (!config) {
    return <Loading message={message || "플랫폼 설정을 불러오는 중…"} />;
  }

  if (config.experience === "VERIFY") {
    return <VerifyApp config={config} client={client} />;
  }

  const publicPrivacyToken = new URLSearchParams(window.location.search).get("token") || "";

  if (
    config.experience === "CENTER"
    && currentPath === "/guardian-consent"
  ) {
    return client
      ? <GuardianConsentPublicPage client={client} token={publicPrivacyToken} />
      : <Loading message="보호자 동의 화면을 준비하는 중…" />;
  }

  if (
    config.experience === "CENTER"
    && currentPath === "/data-share"
  ) {
    return client
      ? <StudentDataSharePublicPage client={client} token={publicPrivacyToken} />
      : <Loading message="공유 화면을 준비하는 중…" />;
  }

  if (
    config.experience === "CENTER"
    && currentPath === "/provider-session"
  ) {
    return client
      ? <ProviderSessionPublicPage client={client} token={publicPrivacyToken} />
      : <Loading message="체험처 확약 화면을 준비하는 중…" />;
  }

  if (!session) {
    return <SignIn config={config} client={client} message={message} />;
  }

  if (!me) {
    return <Loading message={message || "사용자 정보를 연결하는 중…"} />;
  }

  if (!client) {
    return <Loading message="API 연결을 준비하는 중…" />;
  }

  // CENTER-CALM-HOME-01: guarded native entry
  if (
    CENTER_CALM_HOME_ENABLED && config.experience === "CENTER"
    && !centerLegacyHome && !pendingStaffClaim
    && (currentPath === "/today" || isCalmEntry(currentPath, experienceContext?.navigation || []))
  ) {
    const ready = Boolean(experienceContext && currentWorkspace && contextMatches(
      { accountId: session.user.id, personId: me.personId, schoolId: currentWorkspace.organizationId },
      { accountId: me.authUserId, personId: experienceContext.personId,
        schoolId: experienceContext.workspace?.organizationId || "", allowed: experienceContext.allowed }
    ));
    return <CenterCalmHome
      policyClient={client}
      key={`${session.user.id}:${workspaceId}`}
      schoolId={workspaceId}
      schoolName={currentWorkspace?.organizationName || ""}
      ready={ready}
      statusMessage={experienceContext && !experienceContext.allowed
        ? "이 학교에 접근할 권한이 없어요. 다른 학교를 선택해 주세요."
        : !experienceContext && message ? "학교 연결을 확인하지 못했어요. 기존 화면에서 다시 확인해 주세요." : undefined}
      schools={workspaceOptions.map(item => ({ id: item.organizationId, name: item.organizationName }))}
      navigation={ready ? experienceContext!.navigation : []}
      onSchoolChange={(id) => { setExperienceContext(null); setWorkspaceId(id); }}
      onNavigate={navigate}
      onLegacy={() => setCenterLegacyHome(true)}
      onSignOut={async () => {
        const result = await client.auth.signOut();
        if (result.error) throw result.error;
      }}
    />;
  }

  return (
    <div
      className="app-shell"
      data-center-ux-root={config.experience === "CENTER" ? "true" : undefined}
      data-wallet-ux-root={config.experience === "WALLET" ? "true" : undefined}
    >
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">RS</span>
          <div>
            <strong>{appTitle(config.experience)}</strong>
            <small>{environmentLabel(config.environment)}</small>
          </div>
        </div>

        {experienceContext?.allowed && (
          config.experience === "CENTER" ? (
            <CenterGroupedNavigation
              items={experienceContext.navigation}
              currentPath={currentPath}
              onNavigate={navigate}
            />
          ) : (
            <nav>
              {walletNavigationItems.map((item) => (
                <button
                  className={navigationIsActive(config.experience,item.routeKey,item.path,currentPath) ? "nav-item active" : "nav-item"}
                  key={item.routeKey}
                  onClick={() => navigate(item.path)}
                >
                  {config.experience==="WALLET" && <span className="nav-glyph wallet-nav-glyph">{walletNavGlyph(item.routeKey)}</span>}
                  <span>{config.experience==="WALLET" ? walletNavLabel(item.routeKey,item.label) : item.label}</span>
                </button>
              ))}
            </nav>
          )
        )}

        <div className="sidebar-footer">
          {CENTER_CALM_HOME_ENABLED && config.experience === "CENTER" && experienceContext?.allowed && (
            <button onClick={() => { setCenterLegacyHome(false); navigate("/"); }}>검색 홈</button>
          )}
          {config.experience==="WALLET" && (
            <button className="wallet-help-trigger" onClick={()=>setWalletHelpOpen(true)}>
              <span>?</span>
              <span>사용안내 · Q&amp;A</span>
            </button>
          )}
          {config.experience === "CENTER" && experienceContext?.allowed && (
            <button
              className={currentPath === "/help" ? "sidebar-help-button active" : "sidebar-help-button"}
              onClick={() => navigate("/help")}
              aria-current={currentPath === "/help" ? "page" : undefined}
              data-help="사용안내와 자주 묻는 질문, 문제 해결 방법을 확인합니다."
            >
              <span className="sidebar-help-icon">?</span>
              <span>사용안내 · Q&amp;A</span>
            </button>
          )}
          <span>{config.baseDomain}</span>
          <ExperienceSwitch
            config={config}
            availableExperiences={availableExperiences}
            compact
          />
          <button onClick={() => client?.auth.signOut()} data-help="현재 계정에서 안전하게 로그아웃합니다.">로그아웃</button>
        </div>
      </aside>

      {config.experience === "CENTER" && <CenterTooltipLayer />}

      <main className="main">
        <header className="topbar">
          <div>
            <p className="eyebrow">{appSubtitle(config.experience)}</p>
            <h1>
              {config.experience === "WALLET"
                ? me.displayName
                : currentWorkspace?.organizationName || appTitle(config.experience)}
            </h1>
          </div>
          <div className="topbar-actions">
            {session.user.app_metadata?.rs_master_admin === true && (
              <div className="rs-master-admin-chip" aria-label="RS 최고관리자 계정">
                <span>RS</span><strong>MASTER ADMIN</strong>
              </div>
            )}
            {config.experience==="ADMIN" && (
              <button className="admin-ai-trigger" onClick={()=>setAdminAiOpen(true)}>
                <span>AI</span><strong>운영 안내</strong>
              </button>
            )}
            {(config.experience==="CENTER" || config.experience==="WALLET") && experienceContext?.allowed && (
              <button className="experience-ai-trigger" onClick={()=>setExperienceAiOpen(true)}>
                <span>AI</span><strong>{config.experience==="CENTER" ? "업무 안내" : "성장 안내"}</strong>
              </button>
            )}
            {config.experience==="WALLET" && (
              <div className="wallet-future-status" aria-label="Life Passport 활성 상태">
                <i aria-hidden="true" />
                <span>LIFE PASSPORT</span>
                <small>LIVE</small>
              </div>
            )}
            {config.experience==="WALLET" && (
              <button className="wallet-help-mobile-trigger" onClick={()=>setWalletHelpOpen(true)}>
                ? 도움말
              </button>
            )}
            {config.experience==="CENTER" && currentWorkspace && <CenterNotificationBell client={client} schoolId={currentWorkspace.organizationId} />}
            <div className="identity">
              <strong>{me.displayName || session.user.email}</strong>
              <small>{me.email || session.user.email}</small>
            </div>
          </div>
        </header>

        {(config.experience === "CENTER" || config.experience === "ADMIN") && (
          <section className="workspace-row">
            <label>
              작업공간
              <select value={workspaceId} onChange={(event) => setWorkspaceId(event.target.value)}>
                <option value="">
                  {config.experience === "CENTER" ? "학교 작업공간 선택" : "작업공간 선택"}
                </option>
                {workspaceOptions.map((workspace) => (
                  <option key={workspace.membershipId} value={workspace.organizationId}>
                    {workspace.organizationName} · {workspace.organizationType}
                  </option>
                ))}
              </select>
            </label>

            <div className="role-chips">
              {(currentWorkspace?.roles || []).map((role) => (
                <span key={`${role.roleKey}-${role.scopeType}-${role.scopeRef || ""}`}>
                  {roleLabel(role.roleKey)}
                </span>
              ))}
            </div>
          </section>
        )}

        {pendingStaffClaim && (config.experience === "CENTER" || config.experience === "ADMIN") ? (
          <StaffAccessClaimPage client={client} />
        ) : pendingBadgeEmailClaim && config.experience === "WALLET" ? (
          <WalletBadgeEmailClaimPage client={client} token={pendingBadgeEmailClaim} />
        ) : !experienceContext ? (
          <Loading message={message || "접근 권한을 확인하는 중…"} />
        ) : !experienceContext.allowed ? (
          config.experience === "WALLET" && experienceContext.reason === "LEARNER_SELF_REQUIRED" ? (
            <WalletClaimPage client={client} />
          ) : (
            <AccessDenied
              context={experienceContext}
              config={config}
              availableExperiences={availableExperiences}
            />
          )
        ) : (
          <RouteContent
            experience={config.experience}
            context={experienceContext}
            currentPath={currentPath}
            workspace={currentWorkspace}
            client={client}
            onNavigate={navigate}
            onUseInPortfolio={useWalletRecordInPortfolio}
          />
        )}
      </main>

      {config.experience==="ADMIN" && (
        <AdminAiGuide
          client={client}
          open={adminAiOpen}
          currentPath={currentPath}
          rootOrganizationId={currentWorkspace?.organizationId || null}
          navigation={experienceContext?.navigation || []}
          onClose={()=>setAdminAiOpen(false)}
          onNavigate={navigate}
        />
      )}

      {(config.experience==="CENTER" || config.experience==="WALLET") && experienceContext?.allowed && (
        <ExperienceAiGuide
          client={client}
          experience={config.experience}
          open={experienceAiOpen}
          currentPath={currentPath}
          workspaceId={config.experience==="CENTER" ? currentWorkspace?.organizationId || null : null}
          navigation={experienceContext.navigation || []}
          onClose={()=>setExperienceAiOpen(false)}
          onNavigate={navigate}
        />
      )}

      {config.experience==="WALLET" && (
        <WalletHelpCenter
          open={walletHelpOpen}
          onClose={()=>setWalletHelpOpen(false)}
          onNavigate={navigate}
        />
      )}
    </div>
  );
}

function SignIn({
  config,
  client,
  message
}: {
  config: RuntimeConfig;
  client: PlatformClient | null;
  message: string;
}) {
  const [busy, setBusy] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [errorText, setErrorText] = useState(message);
  const badgeClaimMode =
    config.experience === "WALLET"
    && Boolean(window.sessionStorage.getItem(BADGE_EMAIL_CLAIM_KEY));
  const walletClaimMode =
    config.experience === "WALLET"
    && !badgeClaimMode
    && Boolean(window.sessionStorage.getItem(WALLET_CLAIM_KEY));

  async function googleSignIn() {
    if (!client) return;

    setBusy(true);
    setErrorText("");

    const returnPath = normalizePath(window.location.pathname);
    window.sessionStorage.setItem(AUTH_RETURN_KEY, returnPath);
    window.sessionStorage.removeItem(RS_ADMIN_ENTRY_KEY);

    const { error } = await client.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin
      }
    });

    if (error) {
      setErrorText(error.message);
      setBusy(false);
    }
  }

  async function rsAdminSignIn() {
    if (!client || !adminPassword) return;

    setAdminBusy(true);
    setErrorText("");

    const returnPath = normalizePath(window.location.pathname);
    window.sessionStorage.setItem(AUTH_RETURN_KEY, returnPath);
    window.sessionStorage.setItem(RS_ADMIN_ENTRY_KEY, "1");

    const { data, error } = await client.auth.signInWithPassword({
      email: RS_MASTER_ADMIN_EMAIL,
      password: adminPassword
    });

    if (error || !data.user) {
      window.sessionStorage.removeItem(RS_ADMIN_ENTRY_KEY);
      setErrorText(error?.message || "RS 관리자 로그인에 실패했습니다.");
      setAdminBusy(false);
      return;
    }

    if (data.user.app_metadata?.rs_master_admin !== true) {
      window.sessionStorage.removeItem(RS_ADMIN_ENTRY_KEY);
      await client.auth.signOut();
      setErrorText("RS 최고관리자 권한이 확인되지 않았습니다.");
      setAdminBusy(false);
      return;
    }

    setAdminPassword("");
  }

  const title = badgeClaimMode
    ? "Life Stamp 받기"
    : walletClaimMode
      ? "Life Passport 연결"
      : appTitle(config.experience);

  const description = badgeClaimMode
    ? "안내 메일을 받은 Google 계정으로 계속하면 Life Stamp가 내 Life Passport에 자동으로 연결됩니다."
    : walletClaimMode
      ? "Google 계정으로 계속한 뒤 한 번만 확인하면 학교에서 쌓인 진로 기록이 내 Life Passport로 연결됩니다."
      : "권한이 부여된 작업공간으로 이동하려면 로그인하세요.";

  const buttonLabel = busy
    ? "Google에 연결 중…"
    : badgeClaimMode
      ? "Google로 확인하고 바로 받기"
      : walletClaimMode
        ? "Google로 계속해서 연결"
        : "Google로 계속하기";

  return (
    <div className="auth-page">
      <div className="auth-card">
        <span className="brand-mark large">RS</span>
        <p className="eyebrow">
          {badgeClaimMode ? "LIFE STAMP ARRIVAL" : appSubtitle(config.experience)}
        </p>
        <h1>{title}</h1>
        <p className="muted">{description}</p>
        {(badgeClaimMode || walletClaimMode) && (
          <div className="claim-trust-note">
            <strong>연결 코드 입력 없음</strong>
            <span>
              {badgeClaimMode
                ? "Google 로그인 후 발급 대상과 이메일을 확인해 자동으로 연결합니다."
                : "선생님이 보낸 링크에 연결 정보가 이미 포함되어 있습니다."}
            </span>
          </div>
        )}
        <button className="primary" onClick={googleSignIn} disabled={!client || busy || adminBusy}>
          {buttonLabel}
        </button>

        {!badgeClaimMode && !walletClaimMode && (
          <>
            <div className="rs-admin-entry-divider"><span>RS 운영자</span></div>
            {!adminMode ? (
              <button className="rs-admin-entry-button" onClick={()=>{setAdminMode(true);setErrorText("");}} disabled={!client || busy || adminBusy}>
                <span className="rs-admin-entry-mark">RS</span>
                <span className="rs-admin-entry-copy">
                  <strong>RS 관리자로 입장</strong>
                  <small>Admin · Center · Wallet 통합 최고관리자</small>
                </span>
                <b>→</b>
              </button>
            ) : (
              <form className="rs-admin-login-form" onSubmit={(event)=>{event.preventDefault();void rsAdminSignIn();}}>
                <div className="rs-admin-login-head">
                  <span className="rs-admin-entry-mark">RS</span>
                  <span><strong>RS 전용 최고관리자</strong><small>{RS_MASTER_ADMIN_EMAIL}</small></span>
                  <button type="button" className="rs-admin-login-close" onClick={()=>{setAdminMode(false);setAdminPassword("");setErrorText("");}}>×</button>
                </div>
                <label>관리자 비밀번호
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={adminPassword}
                    onChange={(event)=>setAdminPassword(event.target.value)}
                    placeholder="비밀번호 입력"
                    autoFocus
                  />
                </label>
                <button className="rs-admin-login-submit" type="submit" disabled={!client || busy || adminBusy || !adminPassword}>
                  {adminBusy ? "권한 확인 중…" : "최고관리자로 로그인"}
                </button>
              </form>
            )}
            <p className="rs-admin-entry-note">전용 관리자 계정은 일반 Google 로그인과 분리되어 있으며, 서버의 RS MASTER ADMIN 권한을 다시 확인한 뒤 입장합니다.</p>
          </>
        )}

        {errorText && <p className="error">{errorText}</p>}
        <small className="domain-note">{window.location.hostname}</small>
      </div>
    </div>
  );
}

function ExperienceSwitch({
  config,
  availableExperiences,
  compact = false
}: {
  config: RuntimeConfig;
  availableExperiences: AvailableExperience[];
  compact?: boolean;
}) {
  const allowed = availableExperiences.filter((item) => item.allowed);

  if (allowed.length === 0) return null;

  return (
    <div className={compact ? "experience-switch compact" : "experience-switch"}>
      {allowed.map((item) => {
        const domain = config.domains[item.domainKey];
        if (!domain) return null;

        return (
          <a key={item.experience} href={domain.url}>
            {experienceLabel(item.experience)}
          </a>
        );
      })}
    </div>
  );
}

type CenterNavGroup = {
  key: "school" | "students" | "learning" | "support" | "outcomes";
  label: string;
  description: string;
  icon: string;
  routeKeys: string[];
};

const CENTER_NAV_GROUPS: CenterNavGroup[] = [
  {
    key: "school",
    label: "오늘 할 일",
    description: "오늘 처리할 업무와 학교 운영현황",
    icon: "▣",
    routeKeys: ["operations", "reports"]
  },
  {
    key: "students",
    label: "학생 기록",
    description: "학생 찾기부터 학적·상담까지",
    icon: "◎",
    routeKeys: ["learners", "student-lifecycle", "roster", "classes"]
  },
  {
    key: "learning",
    label: "검사·활동",
    description: "진로검사와 수업·체험 운영",
    icon: "◇",
    routeKeys: ["assessments", "programs"]
  },
  {
    key: "support",
    label: "학생 지원",
    description: "Case·동의·체험처 등 지원업무",
    icon: "◉",
    routeKeys: ["assurance", "caseload", "privacy", "partners"]
  },
  {
    key: "outcomes",
    label: "성과·배지",
    description: "활동 검토부터 디지털배지까지",
    icon: "✦",
    routeKeys: ["evidence", "badges"]
  }
];

function centerGroupForPath(items: NavigationItem[], currentPath: string) {
  if (currentPath === "/evaluation") return "outcomes";
  if (currentPath === "/journeys") return "learning";

  const currentItem = items.find((item) =>
    navigationIsActive("CENTER", item.routeKey, item.path, currentPath)
  );
  if (!currentItem) return null;

  return CENTER_NAV_GROUPS.find((group) => group.routeKeys.includes(currentItem.routeKey))?.key ?? null;
}

function CenterGroupedNavigation({
  items,
  currentPath,
  onNavigate
}: {
  items: NavigationItem[];
  currentPath: string;
  onNavigate: (path: string) => void;
}) {
  const activeGroup = centerGroupForPath(items, currentPath);
  const [openGroups, setOpenGroups] = useState<string[]>(() =>
    activeGroup ? [activeGroup] : ["school"]
  );

  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((current) =>
      current.includes(activeGroup) ? current : [...current, activeGroup]
    );
  }, [activeGroup]);

  function toggleGroup(key: string) {
    setOpenGroups((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    );
  }

  const homeItem = items.find((item) => item.routeKey === "today");
  const knownRouteKeys = new Set([
    "today",
    ...CENTER_NAV_GROUPS.flatMap((group) => group.routeKeys)
  ]);
  const ungroupedItems = items.filter((item) => !knownRouteKeys.has(item.routeKey));

  return (
    <nav className="center-grouped-nav" aria-label="센터 주요 메뉴">
      {homeItem && (() => {
        const copy = centerFriendlyNavCopy(homeItem.routeKey, homeItem.label, homeItem.description);
        return (
          <button
            className={navigationIsActive("CENTER", homeItem.routeKey, homeItem.path, currentPath)
              ? "nav-item center-home-nav active"
              : "nav-item center-home-nav"}
            onClick={() => onNavigate(homeItem.path)}
            data-help={copy.help}
          >
            <span className="nav-glyph">{centerNavGlyph(homeItem.routeKey)}</span>
            <span className="center-nav-copy">
              <strong>{copy.label}</strong>
              <small>{copy.description}</small>
            </span>
          </button>
        );
      })()}

      <p className="center-nav-hint">기능을 외우지 않아도 됩니다. 메뉴에 마우스를 올리면 간단한 설명이 나옵니다.</p>

      <div className="center-nav-groups">
        {CENTER_NAV_GROUPS.map((group) => {
          const groupItems = group.routeKeys
            .map((routeKey) => items.find((item) => item.routeKey === routeKey))
            .filter((item): item is NavigationItem => Boolean(item));

          if (groupItems.length === 0) return null;

          const isOpen = openGroups.includes(group.key);
          const hasActiveItem = groupItems.some((item) =>
            navigationIsActive("CENTER", item.routeKey, item.path, currentPath)
          ) || activeGroup === group.key;

          return (
            <section
              className={hasActiveItem ? "center-nav-group active" : "center-nav-group"}
              key={group.key}
            >
              <button
                className="center-nav-group-toggle"
                type="button"
                aria-expanded={isOpen}
                onClick={() => toggleGroup(group.key)}
                data-help={group.description}
              >
                <span className="center-nav-group-title">
                  <span className="center-nav-group-icon">{group.icon}</span>
                  <span>{group.label}</span>
                </span>
                <span className="center-nav-group-count">{groupItems.length}</span>
                <span className="center-nav-chevron" aria-hidden="true">{isOpen ? "−" : "+"}</span>
              </button>

              <div className={isOpen ? "center-nav-group-items open" : "center-nav-group-items"}>
                {groupItems.map((item) => {
                  const copy = centerFriendlyNavCopy(item.routeKey, item.label, item.description);
                  return (
                    <button
                      className={navigationIsActive("CENTER",item.routeKey,item.path,currentPath) ? "nav-item active" : "nav-item"}
                      key={item.routeKey}
                      onClick={() => onNavigate(item.path)}
                      data-help={copy.help}
                    >
                      <span className="nav-glyph">{centerNavGlyph(item.routeKey)}</span>
                      <span className="center-nav-copy">
                        <strong>{copy.label}</strong>
                        <small>{copy.description}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {ungroupedItems.length > 0 && (
        <div className="center-nav-ungrouped">
          {ungroupedItems.map((item) => {
            const copy = centerFriendlyNavCopy(item.routeKey, item.label, item.description);
            return (
              <button
                className={navigationIsActive("CENTER",item.routeKey,item.path,currentPath) ? "nav-item active" : "nav-item"}
                key={item.routeKey}
                onClick={() => onNavigate(item.path)}
                data-help={copy.help}
              >
                <span className="nav-glyph">{centerNavGlyph(item.routeKey)}</span>
                <span className="center-nav-copy">
                  <strong>{copy.label}</strong>
                  <small>{copy.description}</small>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <button
        className={currentPath === "/help" ? "nav-item center-mobile-help-nav active" : "nav-item center-mobile-help-nav"}
        onClick={() => onNavigate("/help")}
        data-help="사용안내와 자주 묻는 질문을 확인합니다."
      >
        <span className="nav-glyph">?</span>
        <span className="center-nav-copy">
          <strong>도움말</strong>
          <small>사용안내·Q&A</small>
        </span>
      </button>
    </nav>
  );
}

function navigationIsActive(experience:Experience,routeKey:string,path:string,currentPath:string){
  if(currentPath===path)return true;
  return experience==="CENTER" && routeKey==="evidence" && ["/evaluation","/assurance"].includes(currentPath);
}

function centerNavGlyph(routeKey:string){
  const glyphs:Record<string,string>={
    today:"⌂",
    operations:"☑",
    learners:"◎",
    assessments:"◇",
    "student-lifecycle":"↕",
    roster:"⇩",
    classes:"▦",
    programs:"□",
    badges:"✦",
    evidence:"✓",
    reports:"▤",
    assurance:"※",
    caseload:"◉",
    privacy:"◌",
    partners:"✥"
  };
  return glyphs[routeKey] || "·";
}

function walletNavLabel(routeKey:string,fallback:string){
  const labels:Record<string,string>={
    journey:"홈",
    experiences:"경험",
    growth:"성장",
    assessments:"검사",
    credentials:"Life Stamp",
    share:"공유",
    "activity-badges":"활동배지",
    inbox:"할 일",
    goals:"목표",
    portfolio:"만들기"
  };
  return labels[routeKey] || fallback;
}

function walletNavGlyph(routeKey:string){
  const glyphs:Record<string,string>={
    journey:"◉",
    experiences:"◇",
    growth:"↗",
    assessments:"◎",
    credentials:"✦",
    share:"↗",
    "activity-badges":"✧",
    inbox:"☑",
    goals:"◎",
    portfolio:"✎"
  };
  return glyphs[routeKey] || "·";
}

function AccessDenied({
  context,
  config,
  availableExperiences
}: {
  context: ExperienceContext;
  config: RuntimeConfig;
  availableExperiences: AvailableExperience[];
}) {
  return (
    <section className="access-card">
      <span className="access-code">403</span>
      <p className="eyebrow">접근 권한</p>
      <h2>이 서비스 영역을 이용할 수 없습니다</h2>
      <p>{accessReasonText(context.reason)}</p>
      <ExperienceSwitch config={config} availableExperiences={availableExperiences} />
    </section>
  );
}

function RouteContent({
  experience,
  context,
  currentPath,
  workspace,
  client,
  onNavigate,
  onUseInPortfolio
}: {
  experience: Experience;
  context: ExperienceContext;
  currentPath: string;
  workspace: Workspace | null;
  client: PlatformClient;
  onNavigate: (path: string) => void;
  onUseInPortfolio: (
    sourceType: "LIFE_STAMP" | "EXPERIENCE" | "COMPETENCY" | "ACHIEVEMENT" | "GOAL" | "ASSESSMENT_RESULT",
    sourceId: string
  ) => void;
}) {
  const experienceDetail = experience === "WALLET"
    ? currentPath.match(/^\/experiences\/(school|personal)\/([0-9a-f-]{36})$/i)
    : null;
  if (experienceDetail) {
    return <WalletExperienceStudioPage
      client={client}
      experienceType={experienceDetail[1].toUpperCase() as "SCHOOL" | "PERSONAL"}
      experienceId={experienceDetail[2]}
      onNavigate={onNavigate}
      onUseInPortfolio={(sourceId) => onUseInPortfolio("EXPERIENCE", sourceId)}
    />;
  }
  if (experience === "WALLET" && currentPath === "/experiences") {
    return <WalletExperienceStudioPage client={client} onNavigate={onNavigate} onUseInPortfolio={(sourceId) => onUseInPortfolio("EXPERIENCE", sourceId)} />;
  }
  if (experience === "WALLET" && currentPath === "/activity-badges") {
    return <WalletActivityBadgesPage client={client} onNavigate={onNavigate} />;
  }
  if (experience === "WALLET" && currentPath === "/inbox") {
    return <WalletInboxActionCenter client={client} onNavigate={onNavigate} />;
  }
  if (experience === "WALLET" && currentPath === "/goals") {
    return <WalletGoalPlannerPage client={client} onUseInPortfolio={(goalId) => onUseInPortfolio("GOAL", goalId)} />;
  }
  if (experience === "WALLET" && currentPath === "/portfolio") {
    return <PortfolioStudioPage client={client} />;
  }
  if (experience === "CENTER" && currentPath === "/evaluation" && workspace) {
    return <CenterEvaluationAchievementWorkbench client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && currentPath === "/assurance" && workspace) {
    return <CenterAssurancePage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && currentPath === "/journeys" && workspace) {
    return <CenterJourneysPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && currentPath === "/help" && workspace) {
    return <CenterHelpPage />;
  }

  const route =
    context.navigation.find((item) => item.path === currentPath) ||
    context.navigation[0];

  if (experience === "CENTER" && route.routeKey === "learners" && workspace) {
    return <CenterLearnersPage client={client} schoolId={workspace.organizationId} canCreateLearner={workspace.roles.some((role)=>role.roleKey==="SCHOOL_ADMIN")} />;
  }

  if (experience === "CENTER" && route.routeKey === "today" && workspace) {
    return <CenterTodayPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "operations" && workspace) {
    return <CenterOperationsPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "caseload" && workspace) {
    return <CenterCaseloadPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "privacy" && workspace) {
    return <CenterPrivacyPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "partners" && workspace) {
    return <CenterPartnersPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "student-lifecycle" && workspace) {
    return <CenterStudentLifecyclePage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "roster" && workspace) {
    return <CenterRosterPlanningPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "classes" && workspace) {
    return <CenterClassCounselingPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "assessments" && workspace) {
    return <CenterAssessmentsPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "evidence" && workspace) {
    return <CenterEvidenceWorkbench client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "evaluation" && workspace) {
    return <CenterEvaluationAchievementWorkbench client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "assurance" && workspace) {
    return <CenterAssurancePage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "programs" && workspace) {
    return <CenterProgramsPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "journeys" && workspace) {
    return <CenterJourneysPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "badges" && workspace) {
    return <CenterBadgeERPPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "CENTER" && route.routeKey === "reports" && workspace) {
    return <CenterReportsPage client={client} schoolId={workspace.organizationId} />;
  }

  if (experience === "ADMIN" && route.routeKey === "overview") {
    return <AdminOverviewPage client={client} />;
  }

  if (experience === "ADMIN" && route.routeKey === "control-tower" && workspace) {
    return <AdminControlTowerPage client={client} rootOrganizationId={workspace.organizationId} canManagePlatformAccess={workspace.roles.some((role)=>role.roleKey==="PLATFORM_ADMIN")} />;
  }

  if (experience === "ADMIN" && route.routeKey === "school-rollout" && workspace) {
    return <AdminSchoolRollout client={client} rootOrganizationId={workspace.organizationId} />;
  }

  if (experience === "ADMIN" && route.routeKey === "data-migration" && workspace) {
    return <AdminDataMigrationCenter client={client} rootOrganizationId={workspace.organizationId} />;
  }

  if (experience === "ADMIN" && route.routeKey === "analytics" && workspace) {
    return <AdminExecutiveAnalytics client={client} rootOrganizationId={workspace.organizationId} />;
  }

  if (experience === "ADMIN" && route.routeKey === "privacy-governance" && workspace) {
    return <AdminPrivacyGovernance client={client} rootOrganizationId={workspace.organizationId} />;
  }

  if (experience === "ADMIN" && route.routeKey === "communications" && workspace) {
    return <AdminCommunicationsCenter client={client} rootOrganizationId={workspace.organizationId} />;
  }

  if (experience === "ADMIN" && route.routeKey === "service-desk" && workspace) {
    return <AdminServiceDeskPage client={client} rootOrganizationId={workspace.organizationId} />;
  }

  if (experience === "ADMIN" && route.routeKey === "knowledge-base" && workspace) {
    return <AdminKnowledgeBasePage client={client} rootOrganizationId={workspace.organizationId} />;
  }

  if (experience === "ADMIN" && route.routeKey === "contracts" && workspace) {
    return <AdminTenantContractCenter client={client} rootOrganizationId={workspace.organizationId} />;
  }

  if (experience === "ADMIN" && route.routeKey === "billing" && workspace) {
    return <AdminBillingCenter client={client} rootOrganizationId={workspace.organizationId} />;
  }

  if (experience === "ADMIN" && route.routeKey === "organizations") {
    return <AdminOrganizationsPage client={client} />;
  }

  if (experience === "ADMIN" && route.routeKey === "access") {
    return <AdminAccessPage client={client} />;
  }

  if (experience === "ADMIN" && route.routeKey === "assessment-studio") {
    return <AdminAssessmentStudio client={client} />;
  }

  if (experience === "ADMIN" && route.routeKey === "competencies") {
    return <AdminCompetencyStudio client={client} />;
  }

  if (experience === "ADMIN" && route.routeKey === "credentials") {
    return (
      <>
        <AdminActivityBadgeCertification client={client} />
        <AdminCredentialQueue client={client} />
      </>
    );
  }

  if (experience === "ADMIN" && route.routeKey === "assurance") {
    return <AdminAssuranceGovernance client={client} />;
  }

  if (experience === "ADMIN" && route.routeKey === "system-operations") {
    return <AdminSystemOperations client={client} />;
  }

  if (experience === "ADMIN" && route.routeKey === "platform-configuration") {
    return <AdminPlatformConfiguration client={client} />;
  }

  if (experience === "ADMIN" && route.routeKey === "ai-cost") {
    return <AdminAiCostPage client={client} />;
  }

  if (experience === "ADMIN" && route.routeKey === "audit") {
    return <AdminSecurityControlCenter client={client} />;
  }

  if (experience === "WALLET") {
    if (route.routeKey === "journey") return <WalletJourneyTimelinePage client={client} onNavigate={onNavigate} />;
    if (route.routeKey === "experiences") return <WalletExperienceStudioPage client={client} onNavigate={onNavigate} onUseInPortfolio={(id) => onUseInPortfolio("EXPERIENCE", id)} />;
    if (route.routeKey === "growth") return (
      <WalletGrowthMapPage
        client={client}
        onNavigate={onNavigate}
        onUseInPortfolio={(sourceType, sourceId) => onUseInPortfolio(sourceType, sourceId)}
      />
    );
    if (route.routeKey === "assessments") return (
      <WalletAssessmentsPage
        client={client}
        onNavigate={onNavigate}
        onUseInPortfolio={(resultId) => onUseInPortfolio("ASSESSMENT_RESULT", resultId)}
      />
    );
    if (route.routeKey === "credentials") return <WalletCredentialsPage client={client} onUseInPortfolio={(id) => onUseInPortfolio("LIFE_STAMP", id)} />;
    if (route.routeKey === "share") return <WalletSharePrivacyStudio client={client} />;
  }

  const cards =
    experience === "ADMIN"
      ? [
          ["사용자·권한", "사용자 → 계정 → 소속 → 역할"],
          ["심리검사 스튜디오", "검사도구의 문항·버전·검토·발행"],
          ["정책·검토", "근거 → 규칙 → 적용범위 → 사람의 최종 검토"],
          ["감사·이력", "상관관계 ID 기반 전체 흐름 추적"]
        ]
      : experience === "CENTER"
        ? [
            ["학생", "담당 학생 범위 안에서만 접근"],
            ["프로그램", "계획 → 프로그램 → 활동 운영"],
            ["활동 증빙", "검증된 활동 결과와 증빙 관리"],
            ["디지털 자격증명", "기준·증빙 검토 후 발급"]
          ]
        : [
            ["진로성장 여정", "시간에 따라 이어지는 성장 기록"],
            ["나의 경험", "참여 활동과 성찰 기록"],
            ["디지털 자격증명", "확인된 성취와 자격증명"],
            ["공유", "공개 가능한 정보만 안전하게 공유"]
          ];

  return (
    <section>
      <div className="section-heading">
        <div>
          <p className="eyebrow">기능 영역</p>
          <h2>{route.label}</h2>
          <p className="route-description">{route.description}</p>
        </div>
        <span className="phase-badge">개발 · BUILD-02</span>
      </div>

      <div className="context-strip">
        <span>서비스 <strong>{experienceLabel(experience)}</strong></span>
        {workspace && <span>작업공간 <strong>{workspace.organizationName}</strong></span>}
        <span>접근권한 <strong>서버 검증 완료</strong></span>
      </div>

      <div className="grid">
        {cards.map(([title, description], index) => (
          <article className="feature-card" key={title}>
            <span className="feature-index">{String(index + 1).padStart(2, "0")}</span>
            <h3>{title}</h3>
            <p>{description}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function VerifyApp({
  config,
  client
}: {
  config: RuntimeConfig;
  client: PlatformClient | null;
}) {
  const [credentialId, setCredentialId] = useState("");
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [message, setMessage] = useState("");
  const [verifying, setVerifying] = useState(false);

  async function lookup(value: string) {
    if (!client || !value.trim()) return;

    setVerifying(true);
    setMessage("");
    setResult(null);

    const { data, error } = await client.rpc("verify_credential_public", {
      p_external_credential_id: value.trim()
    });

    setVerifying(false);

    if (error) {
      setMessage(error.message);
      return;
    }

    const row = Array.isArray(data) ? (data[0] as VerifyResult | undefined) : undefined;
    if (!row) {
      setMessage("해당 자격증명을 찾을 수 없습니다.");
      return;
    }

    setResult(row);
  }

  useEffect(() => {
    if (!client) return;

    const value = new URLSearchParams(window.location.search).get("credential");
    if (!value) return;

    setCredentialId(value);
    void lookup(value);
  }, [client]);

  async function verify(event: React.FormEvent) {
    event.preventDefault();
    await lookup(credentialId);
  }

  const shareMatch = window.location.pathname.match(/^\/share\/([0-9a-f-]{36})$/i);
  if (shareMatch) {
    return <PublicWalletSharePage client={client} shareId={shareMatch[1]} />;
  }

  return (
    <div className="verify-page">
      <header className="verify-header">
        <span className="brand-mark">RS</span>
        <div>
          <strong>RS 자격증명 검증</strong>
          <small>{config.baseDomain}</small>
        </div>
      </header>

      <main className="verify-main">
        <p className="eyebrow">공개 검증</p>
        <h1>디지털 자격증명 확인</h1>
        <p className="muted">
          공개 가능한 자격증명 상태만 확인합니다. 학생의 민감정보와 원본 기록은 이 화면에 노출하지 않습니다.
        </p>

        <form className="verify-form" onSubmit={verify}>
          <input
            value={credentialId}
            onChange={(event) => setCredentialId(event.target.value)}
            placeholder="자격증명 ID"
            autoComplete="off"
          />
          <button className="primary" type="submit" disabled={!client || verifying}>{verifying ? "확인 중…" : "확인하기"}</button>
        </form>

        {message && <p className="error">{message}</p>}

        {result && (
          <article className={result.is_valid ? "credential-card valid" : "credential-card invalid"}>
            <div className="credential-status">
              <span>{credentialStatusLabel(result.status, result.is_valid, result.expires_at)}</span>
            </div>
            <h2>{result.credential_name}</h2>
            <p>{result.achievement_name}</p>
            <dl>
              <div><dt>발급기관</dt><dd>{result.issuer_name}</dd></div>
              <div><dt>발급일</dt><dd>{new Date(result.issued_at).toLocaleDateString("ko-KR")}</dd></div>
              <div><dt>상태</dt><dd>{credentialStatusLabel(result.status, result.is_valid, result.expires_at)}</dd></div>
            </dl>
          </article>
        )}
      </main>
    </div>
  );
}

function Loading({ message }: { message: string }) {
  return (
    <div className="loading-page">
      <span className="spinner" />
      <p>{message}</p>
    </div>
  );
}
